import { useCallback, useEffect, useState } from 'react';
import { computeRefundAmount } from '@sushimai/core';
import { api } from '../lib/api.ts';
import { baht, clock } from '../lib/format.ts';
import { openDrawer, printBillSlip, whereLabel } from '../actions.ts';
import { useStore } from '../store.tsx';
import { Alert, AsyncButton, Field, Spinner, useToast } from '../ui.tsx';
import type { RefundResult, SaleDetail } from '../lib/types.ts';

/**
 * ใบลดหนี้ — คืนเงินให้ลูกค้า
 *
 * ★ ฟีเจอร์ที่ระบบเดิมกดแล้วไม่เคยทำงานเลยสักครั้งใน 2 ปีครึ่ง ★
 *
 * ★ ยอดคืนเฉลี่ยส่วนลดลงไปด้วย ★
 * บิล 850 ลด 10% เหลือ 765 ถ้าคืนของราคาเต็ม 170 เท่ากับคืนเกินกว่าที่
 * ลูกค้าจ่ายมาจริง ต้องคืน 153 — เซิร์ฟเวอร์เป็นคนคำนวณ หน้าจอแค่แสดงตัวอย่าง
 * ด้วยสูตรตัวเดียวกันจาก @sushimai/core
 */
export function RefundScreen({
  saleId,
  onDone,
  onBack,
}: {
  saleId: number;
  onDone: () => void;
  onBack: () => void;
}) {
  const store = useStore();
  const toast = useToast();

  const [sale, setSale] = useState<SaleDetail | null>(null);
  const [scope, setScope] = useState<'full' | 'partial'>('full');
  const [picked, setPicked] = useState<Record<number, number>>({});
  const [methodId, setMethodId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RefundResult | null>(null);

  const load = useCallback(async () => {
    try {
      const detail = await api.get<SaleDetail>(`/sales/${saleId}`);
      setSale(detail);

      // ตั้งค่าเริ่มต้นเป็นช่องทางที่ลูกค้าจ่ายมามากที่สุด — คืนทางเดิมเป็นเรื่องปกติที่สุด
      // บิลที่แยกจ่าย การหยิบแถวแรกมาใช้เป็นการเดาที่ไม่มีเหตุผลรองรับ
      const biggest = detail.payments
        .filter((p) => p.kind === 'payment')
        .reduce<(typeof detail.payments)[number] | null>(
          (best, p) => (best === null || p.amountSatang > best.amountSatang ? p : best),
          null,
        );
      const match = store.paymentMethods.find((m) => m.code === biggest?.methodCode);
      setMethodId(match?.id ?? store.paymentMethods[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดบิลไม่สำเร็จ');
    }
  }, [saleId, store.paymentMethods]);

  useEffect(() => { void load(); }, [load]);

  if (!sale) return error ? <div className="page"><Alert>{error}</Alert></div> : <Spinner />;

  const refundable = sale.lines.filter((l) => !l.voided && l.qty > l.refundedQty);
  const alreadyRefunded = Math.max(0, sale.totalSatang - remainingOf(sale));

  const grossSatang = Object.entries(picked).reduce((sum, [lineId, qty]) => {
    const line = sale.lines.find((l) => l.id === Number(lineId));
    return sum + (line ? line.unitPriceSatang * qty : 0);
  }, 0);

  let preview = 0;
  let previewError: string | null = null;
  try {
    preview = computeRefundAmount({
      scope,
      grossSatang,
      subtotalSatang: sale.subtotalSatang,
      totalSatang: sale.totalSatang,
      alreadyRefundedSatang: alreadyRefunded,
    });
  } catch (err) {
    previewError = err instanceof Error ? err.message : null;
  }

  const canSubmit =
    reason.trim() !== '' &&
    methodId !== null &&
    previewError === null &&
    (scope === 'full' || grossSatang > 0);

  const submit = async () => {
    setError(null);
    try {
      const refund = await api.post<RefundResult>(`/sales/${saleId}/refund`, {
        scope,
        lines:
          scope === 'partial'
            ? Object.entries(picked)
                .filter(([, qty]) => qty > 0)
                .map(([lineId, qty]) => ({ saleLineId: Number(lineId), qty }))
            : [],
        paymentMethodId: methodId,
        reason: reason.trim(),
      });
      setResult(refund);

      // คืนเงินสดต้องเปิดลิ้นชัก เพราะต้องหยิบเงินออกให้ลูกค้า
      const problem = await openDrawer(store, refund.shouldOpenDrawer, refund.drawerReason);
      if (problem) toast(problem, 'warn');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'คืนเงินไม่สำเร็จ');
    }
  };

  if (result) {
    return (
      <div className="page" style={{ maxWidth: 460 }}>
        <h1>คืนเงินแล้ว</h1>
        <p className="lede">ใบลดหนี้ {result.creditNoteNo}</p>

        <div className="alert ok" style={{ fontSize: '1.5rem', fontWeight: 700 }}>
          คืนให้ลูกค้า {baht(result.amountSatang)} บาท
        </div>

        <div className="total-row">
          <span>คืนไปแล้วทั้งหมด</span>
          <span className="v">{baht(result.totalRefundedSatang)}</span>
        </div>
        <div className="total-row">
          <span>เหลือคืนได้อีก</span>
          <span className="v">{baht(result.remainingSatang)}</span>
        </div>

        <button className="btn big wide" style={{ marginTop: 24 }} onClick={onDone}>เสร็จ</button>
      </div>
    );
  }

  const isRefunded = sale.status === 'refunded_full';

  return (
    <div className="page">
      <div className="cart-head" style={{ padding: '0 0 14px' }}>
        <button className="btn quiet" onClick={onBack}>←</button>
        <div style={{ flex: 1 }}>
          <strong>{whereLabel(store, sale)}</strong>
          <div style={{ fontSize: '.85rem', color: 'var(--ink-3)' }}>
            {sale.receiptNo ?? sale.billNo} · {clock(sale.paidAt ?? sale.openedAt)}
          </div>
        </div>
        <span className="chip">{baht(sale.totalSatang)}</span>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="action-row" style={{ marginBottom: 20 }}>
        <AsyncButton
          className="btn ghost"
          onClick={async () => {
            const problem = await printBillSlip(store, sale, false);
            toast(problem ?? 'พิมพ์ใบเสร็จซ้ำแล้ว', problem ? 'crit' : 'ok');
          }}
        >
          พิมพ์ใบเสร็จซ้ำ
        </AsyncButton>
      </div>

      {isRefunded ? (
        <div className="alert warn">บิลนี้คืนเงินครบแล้ว คืนเพิ่มไม่ได้</div>
      ) : (
        <>
          <h2>คืนเงิน</h2>

          {alreadyRefunded > 0 && (
            <div className="alert warn">
              บิลนี้คืนไปแล้ว {baht(alreadyRefunded)} บาท เหลือคืนได้อีก{' '}
              {baht(sale.totalSatang - alreadyRefunded)} บาท
            </div>
          )}

          <div className="pick-grid" style={{ marginBottom: 18 }}>
            <button className="pick" aria-pressed={scope === 'full'} onClick={() => setScope('full')}>
              คืนทั้งบิล
            </button>
            <button
              className="pick"
              aria-pressed={scope === 'partial'}
              onClick={() => setScope('partial')}
            >
              คืนบางรายการ
            </button>
          </div>

          {scope === 'partial' && (
            <Field label="เลือกรายการที่คืน">
              {refundable.length === 0 ? (
                <div className="empty">ไม่มีรายการที่คืนได้แล้ว</div>
              ) : (
                <div className="rowlist">
                  {refundable.map((line) => {
                    const max = line.qty - line.refundedQty;
                    const qty = picked[line.id] ?? 0;
                    return (
                      <div key={line.id} className="rowitem">
                        <span className="grow">
                          <strong>{line.productName}</strong>
                          <div className="sub">
                            ซื้อ {line.qty} · คืนได้อีก {max} · {baht(line.unitPriceSatang)}/ชิ้น
                          </div>
                        </span>
                        <button
                          className="qty-btn"
                          onClick={() =>
                            setPicked((p) => ({ ...p, [line.id]: Math.max(0, qty - 1) }))
                          }
                        >
                          −
                        </button>
                        <span className="qty-value">{qty}</span>
                        <button
                          className="qty-btn"
                          onClick={() =>
                            setPicked((p) => ({ ...p, [line.id]: Math.min(max, qty + 1) }))
                          }
                        >
                          +
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Field>
          )}

          <Field label="คืนผ่านช่องทาง">
            <div className="pick-grid">
              {store.paymentMethods.map((method) => (
                <button
                  key={method.id}
                  className="pick"
                  aria-pressed={methodId === method.id}
                  onClick={() => setMethodId(method.id)}
                >
                  {method.name}
                </button>
              ))}
            </div>
          </Field>

          <Field label="เหตุผลการคืนเงิน">
            {/* บังคับกรอก — เจ้าของร้านได้รับแจ้งทุกใบพร้อมเหตุผลนี้ */}
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น อาหารผิดรายการ / ลูกค้าไม่รับของ"
            />
          </Field>

          {scope === 'partial' && grossSatang > 0 && (
            <>
              <div className="total-row">
                <span>ราคาเต็มของที่คืน</span>
                <span className="v">{baht(grossSatang)}</span>
              </div>
              {sale.discountSatang > 0 && (
                <div className="total-row discount">
                  <span>หักส่วนลดตามสัดส่วนของบิล</span>
                  <span className="v">−{baht(grossSatang - preview)}</span>
                </div>
              )}
            </>
          )}

          <div className="total-row grand">
            <span>คืนให้ลูกค้า</span>
            <span className="v">{baht(preview)}</span>
          </div>

          {previewError && <Alert>{previewError}</Alert>}

          {sale.discountSatang > 0 && scope === 'partial' && (
            <p style={{ color: 'var(--ink-3)', fontSize: '.88rem' }}>
              บิลนี้มีส่วนลด ยอดคืนจึงเฉลี่ยส่วนลดลงไปด้วย —
              คืนราคาเต็มเท่ากับคืนเกินกว่าที่ลูกค้าจ่ายมาจริง
            </p>
          )}

          <div className="action-row" style={{ marginTop: 22 }}>
            <button className="btn ghost" onClick={onBack}>ไม่คืน</button>
            <AsyncButton className="btn danger" onClick={submit} disabled={!canSubmit}>
              ยืนยันคืนเงิน {baht(preview)}
            </AsyncButton>
          </div>
        </>
      )}
    </div>
  );
}

/** ยอดที่ยังไม่ได้คืนของบิลใบนี้ — คำนวณจาก ledger ที่บิลถืออยู่ */
function remainingOf(sale: SaleDetail): number {
  const refunded = sale.payments
    .filter((p) => p.kind === 'refund')
    .reduce((sum, p) => sum + Math.abs(p.amountSatang), 0);
  return sale.totalSatang - refunded;
}
